// SPDX-License-Identifier: MIT
// Generated by scripts/browser.ts from ../player.ts. Do not edit.
/**
 * Player postMessage protocol (FR-13.3). The Player runs in a sandboxed, cross-origin iframe with no
 * network access (`connect-src 'none'`); the host page sends it everything it plays.
 *
 * Every message is an envelope `{ source: "pulsereel-player", v: 1, type, ... }`. Host requests may
 * carry an `id`; the Player answers each one with `ack { id }` or `error { id, code, message }`.
 */
import * as z from 'zod/mini';
import { BrandSchema } from './brand.ts';
import { ReelSchema } from './reel.ts';
export const PLAYER_PROTOCOL = 'pulsereel-player';
export const PLAYER_PROTOCOL_VERSION = 1;
const envelope = { source: z.literal(PLAYER_PROTOCOL), v: z.literal(PLAYER_PROTOCOL_VERSION) };
const request = /*@__PURE__*/ (() => ({
  ...envelope,
  id: z.optional(z.number().check(z.int()).check(z.nonnegative())),
}))();
/** What the Player plays: a reel, its brand, and the compiled custom scenes it needs. */
export const PlayerBundleSchema = /*@__PURE__*/ (() =>
  z.object({
    reel: ReelSchema,
    brand: BrandSchema,
    /**
     * Compiled custom scenes as JavaScript sources (IIFE bundles that register into
     * `globalThis.__pulsereelScenes`), keyed by scene type. Library scenes need nothing here.
     */
    scenes: z._default(z.record(z.string(), z.string().check(z.maxLength(2000000))), {}),
  }))();
/** Audio as decoded PCM (e.g. from the in-browser soundtrack engine) or an encoded file (WAV/AAC/MP3). */
export const PlayerAudioSchema = /*@__PURE__*/ (() =>
  z.discriminatedUnion('kind', [
    z.object({
      kind: z.literal('pcm'),
      sampleRate: z.number().check(z.int()).check(z.gte(8000)).check(z.lte(192000)),
      channels: z.array(z.instanceof(Float32Array)).check(z.minLength(1)).check(z.maxLength(2)),
    }),
    z.object({ kind: z.literal('encoded'), data: z.instanceof(ArrayBuffer) }),
  ]))();
/** Bounded binary input only: no URL, CSS source or executable font loader. */
export const PlayerFontSchema = /*@__PURE__*/ (() =>
  z.object({
    family: z
      .string()
      .check(z.minLength(1))
      .check(z.maxLength(100))
      .check(z.regex(/^[\p{L}\p{N} _-]+$/u)),
    weight: z
      .string()
      .check(z.maxLength(20))
      .check(z.regex(/^(?:[1-9]\d{0,2}|1000)(?: (?:[1-9]\d{0,2}|1000))?$/)),
    unicodeRange: z
      .string()
      .check(z.minLength(1))
      .check(z.maxLength(8192))
      .check(
        z.regex(
          /^U\+[0-9A-Fa-f]{1,6}(?:-[0-9A-Fa-f]{1,6})?(?:,\s*U\+[0-9A-Fa-f]{1,6}(?:-[0-9A-Fa-f]{1,6})?)*$/,
        ),
      ),
    data: z
      .instanceof(ArrayBuffer)
      .check(z.refine((data) => data.byteLength > 0 && data.byteLength <= 16 * 1024 * 1024)),
  }))();
export const PlayerFontsSchema = /*@__PURE__*/ (() =>
  z
    .array(PlayerFontSchema)
    .check(z.minLength(1))
    .check(z.maxLength(32))
    .check(
      z.refine((fonts) => fonts.reduce((bytes, font) => bytes + font.data.byteLength, 0) <= 32 * 1024 * 1024),
    ))();
export type PlayerFont = z.infer<typeof PlayerFontSchema>;
/** RFC 6902 JSON Patch operations, applied to the loaded reel. */
export const JsonPatchSchema = /*@__PURE__*/ (() =>
  z
    .array(
      z.discriminatedUnion('op', [
        z.object({ op: z.literal('add'), path: z.string(), value: z.unknown() }),
        z.object({ op: z.literal('replace'), path: z.string(), value: z.unknown() }),
        z.object({ op: z.literal('remove'), path: z.string() }),
        z.object({ op: z.literal('move'), from: z.string(), path: z.string() }),
        z.object({ op: z.literal('copy'), from: z.string(), path: z.string() }),
        z.object({ op: z.literal('test'), path: z.string(), value: z.unknown() }),
      ]),
    )
    .check(z.maxLength(500)))();
export const PlayerQualitySchema = /*@__PURE__*/ (() => z.enum(['draft', 'high']))();
export const HostMessageSchema = /*@__PURE__*/ (() =>
  z.discriminatedUnion('type', [
    z.object({
      ...request,
      type: z.literal('load'),
      bundle: PlayerBundleSchema,
      audio: z.optional(PlayerAudioSchema),
      fonts: z.optional(PlayerFontsSchema),
      watermark: z.boolean(),
      quality: PlayerQualitySchema,
      autoplay: z.optional(z.boolean()),
      loop: z.optional(z.boolean()),
    }),
    z.object({ ...request, type: z.literal('play') }),
    z.object({ ...request, type: z.literal('pause') }),
    z.object({ ...request, type: z.literal('seek'), t: z.number().check(z.gte(0)) }),
    /** Loop between a and b seconds; `null` loops the whole reel, `false` turns looping off. */
    z.object({
      ...request,
      type: z.literal('setLoop'),
      range: z.union([
        z.object({ a: z.number().check(z.gte(0)), b: z.number().check(z.gte(0)) }),
        z.null(),
        z.literal(false),
      ]),
    }),
    /** Instant edit preview: patch the reel in place (not saved anywhere). */
    z.object({
      ...request,
      type: z.literal('applyPatch'),
      jsonPatch: JsonPatchSchema,
      fonts: z.optional(PlayerFontsSchema),
    }),
    /** Replace the brand (colours, name, logo) without reloading, e.g. the landing page's instant rebrand. */
    z.object({
      ...request,
      type: z.literal('setBrand'),
      brand: BrandSchema,
      fonts: z.optional(PlayerFontsSchema),
    }),
    /** Replace or drop the soundtrack after an edit that changes the music. */
    z.object({ ...request, type: z.literal('setAudio'), audio: z.nullable(PlayerAudioSchema) }),
    z.object({ ...request, type: z.literal('setMuted'), muted: z.boolean() }),
    /**
     * Ask for the loaded reel's scene sound requests (answered with `soundEvents`, then `ack`), so the
     * host can re-score an edited reel without running scene code outside the sandbox.
     */
    z.object({ ...request, type: z.literal('getSoundEvents') }),
  ]))();
export const SoundEventSchema = /*@__PURE__*/ (() =>
  z.object({
    beat: z.number(),
    sfx: z.enum(['impact', 'whoosh', 'riser', 'blip', 'tick', 'glitch']),
    gain: z.optional(z.number()),
  }))();
export const PlayerSceneInfoSchema = /*@__PURE__*/ (() =>
  z.object({
    id: z.string(),
    type: z.string(),
    beats: z.tuple([z.number(), z.number()]),
  }))();
export const PlayerMessageSchema = /*@__PURE__*/ (() =>
  z.discriminatedUnion('type', [
    /** The iframe is listening; the host can send `load`. */
    z.object({ ...envelope, type: z.literal('hello'), engine: z.string() }),
    z.object({
      ...envelope,
      type: z.literal('ready'),
      duration: z.number(),
      fps: z.number(),
      bpm: z.number(),
      beats: z.number(),
      scenes: z.array(PlayerSceneInfoSchema),
    }),
    z.object({ ...envelope, type: z.literal('ack'), id: z.number() }),
    z.object({
      ...envelope,
      type: z.literal('timeupdate'),
      t: z.number(),
      beat: z.number(),
      sceneIndex: z.number().check(z.int()),
    }),
    z.object({
      ...envelope,
      type: z.literal('state'),
      playing: z.boolean(),
      muted: z.boolean(),
      /** `blocked`: the browser refused to start sound without a gesture inside the Player. */
      audio: z.enum(['none', 'ready', 'blocked']),
    }),
    /** Audio source scheduled in a running context; this does not prove physical speaker output. */
    z.object({
      ...envelope,
      type: z.literal('soundstart'),
      delayMs: z.number().check(z.gte(0)).check(z.lte(1000)),
    }),
    z.object({ ...envelope, type: z.literal('ended') }),
    z.object({
      ...envelope,
      type: z.literal('soundEvents'),
      id: z.number(),
      events: z.array(SoundEventSchema).check(z.maxLength(10000)),
    }),
    z.object({
      ...envelope,
      type: z.literal('perf'),
      fps: z.number(),
      droppedFrames: z.number().check(z.int()),
      /** Internal resolution as a fraction of 1280×720 (lowered automatically when frames drop). */
      scale: z.number(),
    }),
    z.object({
      ...envelope,
      type: z.literal('error'),
      id: z.optional(z.number()),
      code: z.enum([
        'bad_message',
        'not_loaded',
        'invalid_bundle',
        'invalid_patch',
        'scene_failed',
        'webgl_unavailable',
        'audio_failed',
      ]),
      message: z.string(),
    }),
  ]))();
export type PlayerBundle = z.input<typeof PlayerBundleSchema>;
export type PlayerAudio = z.infer<typeof PlayerAudioSchema>;
export type JsonPatch = z.infer<typeof JsonPatchSchema>;
export type PlayerQuality = z.infer<typeof PlayerQualitySchema>;
export type HostMessage = z.input<typeof HostMessageSchema>;
export type PlayerMessage = z.infer<typeof PlayerMessageSchema>;
export type PlayerSceneInfo = z.infer<typeof PlayerSceneInfoSchema>;
export type SoundEvent = z.infer<typeof SoundEventSchema>;
export type PlayerErrorCode = Extract<
  PlayerMessage,
  {
    type: 'error';
  }
>['code'];
